import React from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  CloudArrowUpIcon,
  ChartBarIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Documentation: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* En-tête */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              📚 Documentation
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Guide complet d'utilisation de l'analyseur financier Excel Pariès
            </p>
          </div>

          {/* Sommaire */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">📋 Sommaire</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <a href="#getting-started" className="block text-blue-600 hover:text-blue-800 transition-colors">
                  1. Prise en main
                </a>
                <a href="#file-format" className="block text-blue-600 hover:text-blue-800 transition-colors">
                  2. Format de fichier requis
                </a>
                <a href="#upload-process" className="block text-blue-600 hover:text-blue-800 transition-colors">
                  3. Processus d'upload
                </a>
                <a href="#analysis-features" className="block text-blue-600 hover:text-blue-800 transition-colors">
                  4. Fonctionnalités d'analyse
                </a>
              </div>
              <div className="space-y-2">
                <a href="#charts-visualization" className="block text-blue-600 hover:text-blue-800 transition-colors">
                  5. Graphiques et visualisations
                </a>
                <a href="#export-options" className="block text-blue-600 hover:text-blue-800 transition-colors">
                  6. Options d'export
                </a>
                <a href="#troubleshooting" className="block text-blue-600 hover:text-blue-800 transition-colors">
                  7. Résolution de problèmes
                </a>
                <a href="#best-practices" className="block text-blue-600 hover:text-blue-800 transition-colors">
                  8. Bonnes pratiques
                </a>
              </div>
            </div>
          </div>

          {/* Section 1: Prise en main */}
          <section id="getting-started" className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center mb-4">
              <InformationCircleIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h2 className="text-2xl font-semibold text-gray-800">1. Prise en main</h2>
            </div>
            <div className="prose max-w-none">
              <p className="text-gray-600 mb-4">
                L'analyseur financier Excel Pariès est conçu pour traiter automatiquement vos fichiers de reporting SOCOA 
                et générer des analyses complètes avec graphiques interactifs et rapports IA.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                <div className="flex">
                  <CheckCircleIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-blue-800 font-medium">Avantages clés :</p>
                    <ul className="text-blue-700 text-sm mt-1 space-y-1">
                      <li>• Analyse automatique des données financières</li>
                      <li>• Comparaisons année N vs N-1</li>
                      <li>• Suivi des objectifs en temps réel</li>
                      <li>• Rapports PDF professionnels</li>
                      <li>• Sauvegarde automatique (24h)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Format de fichier */}
          <section id="file-format" className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-green-600 mr-2" />
              <h2 className="text-2xl font-semibold text-gray-800">2. Format de fichier requis</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">📄 Spécifications techniques</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    Format : Excel (.xlsx ou .xls)
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    Taille max : 10 MB
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    Structure : Reporting SOCOA standard
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    Feuilles : SUIVI MENSUEL + hebdomadaires
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">📊 Structure attendue</h3>
                <div className="bg-gray-50 p-4 rounded-lg text-sm">
                  <div className="font-mono">
                    <div>📁 Feuille "SUIVI MENSUEL"</div>
                    <div className="ml-4 text-gray-600">├─ CA par mois (colonnes D-O)</div>
                    <div className="ml-4 text-gray-600">├─ Fréquentation par mois</div>
                    <div className="ml-4 text-gray-600">├─ Objectifs 2025</div>
                    <div className="ml-4 text-gray-600">└─ Données N-1 et N</div>
                    <div className="mt-2">📁 Feuilles hebdomadaires</div>
                    <div className="ml-4 text-gray-600">├─ Semaines 1-22</div>
                    <div className="ml-4 text-gray-600">├─ Semaines 23-39</div>
                    <div className="ml-4 text-gray-600">└─ Semaines 40-52</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Processus d'upload */}
          <section id="upload-process" className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center mb-4">
              <CloudArrowUpIcon className="h-6 w-6 text-purple-600 mr-2" />
              <h2 className="text-2xl font-semibold text-gray-800">3. Processus d'upload</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">1</span>
                </div>
                <h3 className="font-semibold mb-2">Sélection du fichier</h3>
                <p className="text-gray-600 text-sm">
                  Glissez-déposez votre fichier Excel ou cliquez pour le sélectionner
                </p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">2</span>
                </div>
                <h3 className="font-semibold mb-2">Analyse automatique</h3>
                <p className="text-gray-600 text-sm">
                  Le système traite vos données et génère les KPIs automatiquement
                </p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">3</span>
                </div>
                <h3 className="font-semibold mb-2">Visualisation</h3>
                <p className="text-gray-600 text-sm">
                  Accédez immédiatement aux graphiques et analyses interactives
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Fonctionnalités d'analyse */}
          <section id="analysis-features" className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center mb-4">
              <ChartBarIcon className="h-6 w-6 text-indigo-600 mr-2" />
              <h2 className="text-2xl font-semibold text-gray-800">4. Fonctionnalités d'analyse</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">📈 KPIs automatiques</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• CA total annuel et à date (N vs N-1)</li>
                  <li>• Fréquentation et évolutions</li>
                  <li>• Panier moyen et optimisations</li>
                  <li>• Suivi des objectifs 2025</li>
                  <li>• Taux de réalisation en temps réel</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">🤖 Intelligence artificielle</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Synthèse automatique des performances</li>
                  <li>• Prédictions fin d'année</li>
                  <li>• Détection de patterns saisonniers</li>
                  <li>• Recommandations stratégiques</li>
                  <li>• Niveau de confiance des prédictions</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 5: Graphiques */}
          <section id="charts-visualization" className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center mb-4">
              <ChartBarIcon className="h-6 w-6 text-orange-600 mr-2" />
              <h2 className="text-2xl font-semibold text-gray-800">5. Graphiques et visualisations</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold text-orange-800 mb-2">📊 Vue d'ensemble</h3>
                <ul className="text-orange-700 text-sm space-y-1">
                  <li>• Score global de performance</li>
                  <li>• Top/Flop mois</li>
                  <li>• Évolution cumulative</li>
                </ul>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold text-orange-800 mb-2">🔍 Analyses détaillées</h3>
                <ul className="text-orange-700 text-sm space-y-1">
                  <li>• Heatmap performance mensuelle</li>
                  <li>• Variation vs objectifs</li>
                  <li>• Filtres interactifs</li>
                </ul>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold text-orange-800 mb-2">🚀 Prédictif & IA</h3>
                <ul className="text-orange-700 text-sm space-y-1">
                  <li>• Prédictions intelligentes</li>
                  <li>• Micro-graphiques tendances</li>
                  <li>• Patterns saisonniers</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 6: Export */}
          <section id="export-options" className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center mb-4">
              <DocumentArrowDownIcon className="h-6 w-6 text-red-600 mr-2" />
              <h2 className="text-2xl font-semibold text-gray-800">6. Options d'export</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">📄 Export PDF</h3>
                <p className="text-gray-600 mb-3">
                  Génération automatique d'un rapport PDF professionnel incluant :
                </p>
                <ul className="space-y-1 text-gray-600">
                  <li>• KPIs principaux avec formatage</li>
                  <li>• Synthèse automatique</li>
                  <li>• Tableaux de comparaison</li>
                  <li>• En-tête et pied de page personnalisés</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">📋 Autres exports</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    Copie vers presse-papiers
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    Sauvegarde automatique locale
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    Partage de liens (à venir)
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 7: Résolution de problèmes */}
          <section id="troubleshooting" className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-2" />
              <h2 className="text-2xl font-semibold text-gray-800">7. Résolution de problèmes</h2>
            </div>
            <div className="space-y-4">
              <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
                <h3 className="font-semibold text-yellow-800 mb-2">❌ Erreur d'upload</h3>
                <p className="text-yellow-700 mb-2">Solutions :</p>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>• Vérifiez que le fichier est au format Excel (.xlsx/.xls)</li>
                  <li>• Assurez-vous que la taille est &lt; 10MB</li>
                  <li>• Contrôlez que le fichier n'est pas corrompu</li>
                  <li>• Vérifiez votre connexion internet</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-red-400 bg-red-50 p-4">
                <h3 className="font-semibold text-red-800 mb-2">📊 Données manquantes</h3>
                <p className="text-red-700 mb-2">Causes possibles :</p>
                <ul className="text-red-700 text-sm space-y-1">
                  <li>• Structure de fichier non conforme</li>
                  <li>• Noms de feuilles incorrects</li>
                  <li>• Colonnes déplacées ou manquantes</li>
                  <li>• Format de dates non reconnu</li>
                </ul>
              </div>

              <div className="border-l-4 border-blue-400 bg-blue-50 p-4">
                <h3 className="font-semibold text-blue-800 mb-2">🔄 Données expirées</h3>
                <p className="text-blue-700 text-sm">
                  Les données sont automatiquement supprimées après 24h pour des raisons de sécurité. 
                  Re-uploadez votre fichier pour une nouvelle analyse.
                </p>
              </div>
            </div>
          </section>

          {/* Section 8: Bonnes pratiques */}
          <section id="best-practices" className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center mb-4">
              <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
              <h2 className="text-2xl font-semibold text-gray-800">8. Bonnes pratiques</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">✅ Recommandations</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Utilisez toujours les dernières données</li>
                  <li>• Vérifiez la cohérence avant upload</li>
                  <li>• Sauvegardez vos fichiers sources</li>
                  <li>• Exportez régulièrement en PDF</li>
                  <li>• Consultez les prédictions IA</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">⚠️ À éviter</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Modifier la structure des feuilles</li>
                  <li>• Uploader des fichiers partiels</li>
                  <li>• Ignorer les alertes de validation</li>
                  <li>• Négliger les sauvegardes</li>
                  <li>• Utiliser des données obsolètes</li>
                </ul>
              </div>
            </div>
          </section>

        </motion.div>
      </div>
    </div>
  );
};

export default Documentation; 